import React from 'react';
import PropTypes from 'prop-types';
import cns from 'classnames';
import { NOOP } from '../../../utils/const';
import BreadcrumbsBand from './BreadcrumbsBand';
import Button from '../Button/Button';
import Divider from './Divider';
import DropdownButton from '../DropdownButton/DropdownButton';
import useViewport, { LOW } from '../Hooks/useViewport';

const cssBlock = 'v2-toolbar';

function useToolbarButton(props) {
  const { small, vertical, ...others } = props;
  const { resolution } = useViewport();
  const smallByRes = resolution === LOW;
  const verticalByRes = resolution === LOW;

  return {
    small: smallByRes, vertical: verticalByRes, emphasis: Button.Emphasis.LOW, ...others,
  };
}

/**
 * Bottone Dropdown da utilizzare nella sezione tool del componente `Toolbar`.
 * Le proprietà sono quelle di un `DropdownButton`.
 */
export const ToolbarDropdownButton = (props) => {
  const {
    small, vertical, emphasis, ...others
  } = useToolbarButton(props);
  return <DropdownButton small={small} vertical={vertical} emphasis={emphasis} {...others} />;
};

ToolbarDropdownButton.propTypes = {
  ...DropdownButton.propTypes,
};

ToolbarDropdownButton.defaultProps = {
  ...DropdownButton.defaultProps,
};

/**
 * Bottone da utilizzare nella sezione tool del componente `Toolbar`.
 * Le proprietà sono quelle di un `Button`.
 */
export const ToolbarButton = (props) => {
  const {
    small, vertical, emphasis, ...others
  } = useToolbarButton(props);
  return <Button small={small} vertical={vertical} emphasis={emphasis} {...others} />;
};

ToolbarButton.propTypes = {
  ...Button.propTypes,
};

ToolbarButton.defaultProps = {
  ...Button.defaultProps,
};

/**
 * La toolbar aiuta l'utente ad orientarsi nella navigazione dell'ecositema CGN e
 * permette di effettuare azioni relative al contenuto sottostante.
 *
 * La getione della breadcrumbs Toolbar-Sidebar, come qualsiasi logica relativa alle breadcrumbs
 * non è supportata dal componente.
 *
 */
const Toolbar = ({
  className, children, breadcrumbs, onSelect, onBack,
}) => {
  const classes = cns(cssBlock, className);

  return (
    <div className={classes}>
      <BreadcrumbsBand onBack={onBack} onSelect={onSelect} breadcrumbs={breadcrumbs} />
      <div className={`${cssBlock}__stretch-band`} />
      <div className={`${cssBlock}__tools-band`}>
        {children}
      </div>
    </div>
  );
};

Toolbar.propTypes = {
  /** Classi css personalizzate da aggiungere al componente */
  className: PropTypes.string,

  /**
Callback attivata alla pressione del tasto Back.

Tipicamente la callback deve gestire lo stato delle breadcumbs.
Il primo parametro è la penultima breadcrumbs.
  */
  onBack: PropTypes.func,

  /** Children del componente. Tipicamente è un nodo di tipo ButtonGroup con dei Toolbar.Button e Toolbar.DropdownButton. */
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node,
  ]),

  /**
Callback attivata alla selezione di un breadcrump.
Il primo parametro è la breadcrump.
   */
  onSelect: PropTypes.func,

  /**
Lista delle breadcrumps. Può essere una lista di stringhe o di oggetti con almento le proprietà id e label.
Se un elemento è `selectable` l'utente protrà selezionarlo (default `true`)
   */
  breadcrumbs: PropTypes.arrayOf(
    PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.shape({
        id: PropTypes.string,
        label: PropTypes.string,
        selectable: PropTypes.bool,
      }),
    ]),
  ),
};

Toolbar.defaultProps = {
  className: undefined,
  children: undefined,
  onBack: NOOP,
  onSelect: NOOP,
  breadcrumbs: [],
};

Toolbar.Button = ToolbarButton;
Toolbar.Divider = Divider;
Toolbar.DropdownButton = ToolbarDropdownButton;

export default Toolbar;
